import {Component} from 'react';
import PropTypes from 'prop-types';
import {ContentStatus} from '@livongo/protobuf-grpc-external-education';
import {Program as ProgramType} from '@livongo/protobuf-grpc-external-enrollment/lib/livongo/protobuf/common/user/program_pb';
import Button from 'ui/Button';
import Image from 'ui/Image';
import {withTranslation} from 'react-i18next';
import {FieldTypes} from 'utils/page';
import EnvironmentUtils from '@livongo/utils/environment';
import ButtonReturn from '../navigation/ButtonReturn';
import LessonItem from './LessonItem';
import Popup from './Popup';
import WeighIn from './WeighIn';

const statuses = ContentStatus.Values;
const {HERO_IMAGE, TITLE, DESCRIPTION} = FieldTypes;

class Landing extends Component {
    static propTypes = {
        isOneApp: PropTypes.bool,
        lessonNumber: PropTypes.number.isRequired,
        course: PropTypes.object.isRequired,
        currentLesson: PropTypes.object.isRequired,
        onStartClick: PropTypes.func.isRequired,
        hasUsedScale: PropTypes.bool.isRequired,
        t: PropTypes.func.isRequired,
        isManualTokenExchange: PropTypes.bool.isRequired,
        program: PropTypes.number.isRequired,
    };
    state = {
        showAllLessons: false,
        showPopup: false,
        showWeighInPopup: false,
    };

    constructor(props) {
        super(props);

        const {currentLesson} = props;
        const lessonUserData = currentLesson.getLessonUserData();
        const pagesList = currentLesson.getPagesList();

        this._isCompleted = lessonUserData.getStatus() === statuses.COMPLETED;
        this._isReturning =
            !this._isCompleted &&
            pagesList[0].getPageId() !== lessonUserData.getResumePageId();
        this._imageSource = currentLesson
            .getImageFieldsMap()
            .get(HERO_IMAGE)
            .getImageSrc();
        this._title = currentLesson
            .getTextFieldsMap()
            .get(TITLE)
            .getTextValue();
        this._description = currentLesson
            .getTextFieldsMap()
            .get(DESCRIPTION)
            .getTextValue();
    }

    _onStartLessonClick = () => {
        const {
            course,
            onStartClick,
            hasUsedScale,
            isManualTokenExchange,
            program,
        } = this.props;
        const lessonId = course.getCourseUserData().getResumeLessonId();
        const canStartLesson = course
            .getCourseUserData()
            .getCanStartLesson()
            .getValue();

        if (
            !hasUsedScale &&
            !isManualTokenExchange &&
            program === ProgramType.Values.PREDIABETES
        ) {
            this.setState({
                showWeighInPopup: true,
            });

            return;
        }

        // if user has take a lesson in a week or this lession is already completed (a lesson completed so we only reviewing)
        if (
            canStartLesson ||
            this._isCompleted ||
            program !== ProgramType.Values.PREDIABETES
        ) {
            onStartClick(lessonId);
        } else {
            // only allow 1 lesson per week
            this.setState({
                showPopup: true,
            });
        }
    };

    _onToggleAllLessons = () => {
        this.setState(prevState => ({
            showAllLessons: !prevState.showAllLessons,
        }));
    };

    _onOpenModalClick = () => {
        this.setState({
            showPopup: true,
        });
    };

    _onCloseModalClick = () => {
        this.setState({
            showPopup: false,
        });
    };

    _onCloseWeighInModalClick = () => {
        this.setState({
            showWeighInPopup: false,
        });
    };

    _onOpenWeighInModalClick = () => {
        this.setState({
            showWeighInPopup: true,
        });
    };

    _onCheckAllow = lesson => {
        const {course, program} = this.props;
        const canStartLesson = course
            .getCourseUserData()
            .getCanStartLesson()
            .getValue();

        if (
            canStartLesson ||
            lesson.getStatus() === statuses.COMPLETED ||
            program !== ProgramType.Values.PREDIABETES
        ) {
            return true; // allows go to lesson
        } else {
            return false; // show popup of not allowed
        }
    };

    render() {
        const {
            t,
            isOneApp,
            course,
            lessonNumber,
            onStartClick,
            hasUsedScale,
            isManualTokenExchange,
            program,
        } = this.props;
        const {showAllLessons, showPopup, showWeighInPopup} = this.state;
        let hasUnavailable = false;
        const lessons = course
            .getLessonInfosList()
            .filter(lesson => {
                const currentStatus = lesson.getStatus();
                const isAvailable =
                    currentStatus === statuses.NOT_COMPLETED ||
                    currentStatus === statuses.COMPLETED;

                if (showAllLessons) {
                    return true;
                }

                if (hasUnavailable) {
                    return false;
                }

                hasUnavailable = !isAvailable;

                return true;
            })
            .reverse();
        const showSeeAllButton =
            showAllLessons ||
            lessons.length < course.getLessonInfosList().length - 1;

        return (
            <div className="Landing">
                {showWeighInPopup && !isManualTokenExchange && (
                    <WeighIn onClose={this._onCloseWeighInModalClick} />
                )}
                {showPopup && <Popup onClose={this._onCloseModalClick} />}
                <div className="Landing-container">
                    {!EnvironmentUtils.isMobileDevice() && (
                        <ButtonReturn isOneApp={isOneApp} hasIcon />
                    )}
                    <div className="Landing-content">
                        <div className="Landing-image Image-aspect-container">
                            <Image src={this._imageSource} alt={this._title} />
                        </div>
                        <div className="Landing-description">
                            <p>
                                {t('lesson')} {lessonNumber}
                            </p>
                            <h1 className="heading-md">{this._title}</h1>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: this._description,
                                }}
                            />
                            <Button
                                color="primary"
                                onClick={this._onStartLessonClick}
                            >
                                {this._isCompleted
                                    ? t('review')
                                    : this._isReturning
                                    ? t('resume')
                                    : t('start')}{' '}
                                {t('lesson')}
                            </Button>
                        </div>
                    </div>
                    <div className="Landing-list">
                        <div className="Landing-list-header">
                            <h2 className="heading-sm">Lessons</h2>
                            {showSeeAllButton && (
                                <Button onClick={this._onToggleAllLessons}>
                                    {showAllLessons
                                        ? t('see.less')
                                        : t('see.all')}
                                </Button>
                            )}
                        </div>
                        {lessons.map(lesson => {
                            return (
                                <LessonItem
                                    key={lesson.getLessonId()}
                                    data={lesson}
                                    currentLessonNumber={lessonNumber}
                                    program={program}
                                    onClick={e => {
                                        if (
                                            !hasUsedScale &&
                                            !isManualTokenExchange &&
                                            program ===
                                                ProgramType.Values.PREDIABETES
                                        ) {
                                            this._onOpenWeighInModalClick();
                                        } else {
                                            this._onCheckAllow(lesson)
                                                ? onStartClick(e)
                                                : this._onOpenModalClick();
                                        }
                                    }}
                                />
                            );
                        })}
                    </div>
                    {program === ProgramType.Values.PREDIABETES && (
                        <div className="Landing-note">
                            <p>
                                {t('landingNote')}
                                {hasUnavailable && t('hasUnavailable')}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default withTranslation()(Landing);
